import { useQuery } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { PbBranding } from '@itsrever/rever-api'
import {
  getBranding,
  patchBranding,
  uploadBrandingImage
} from '@/api/settings-api'
import { getSettings } from '@/redux/api/settings-api'
import { useAppDispatch } from '@/redux/hooks'
import { useEcommerceId } from '@/hooks/use-ecommerce-id'
import {
  BrandingTheme,
  PendingBrandingImages,
  emptyBrandingTheme,
  emptyStoredBrandingImages
} from '@/domain/branding'
import {
  useSettingsDraft,
  useSettingsMutation
} from '@/application/manage-settings'
import {
  BrandingSavePlan,
  changedBrandingAttributes,
  toBrandingSavePlan,
  toBrandingTheme,
  toStoredBrandingImages
} from './branding'

const BRANDING_KEY = ['branding']

const brandingKey = (ecommerceId?: string) => [...BRANDING_KEY, ecommerceId]

interface BrandingSave {
  plan: BrandingSavePlan
  editedAttributes: string[]
}

const toStoredBranding = (stored: PbBranding) => ({
  theme: toBrandingTheme(stored),
  images: toStoredBrandingImages(stored)
})

export function useBranding() {
  const ecommerceId = useEcommerceId()
  const query = useQuery({
    queryKey: brandingKey(ecommerceId),
    queryFn: () => getBranding(ecommerceId ?? ''),
    enabled: !!ecommerceId,
    select: toStoredBranding
  })
  const stored = query.data?.theme
  const [branding, setBranding] = useSettingsDraft(stored, emptyBrandingTheme)

  return {
    branding,
    setBranding,
    originalBranding: stored ?? emptyBrandingTheme,
    storedImages: query.data?.images ?? emptyStoredBrandingImages,
    loadingBranding: query.isLoading,
    brandingFailed: query.isError && !query.data,
    reloadBranding: query.refetch
  }
}

export function useSaveBranding() {
  const ecommerceId = useEcommerceId()
  const dispatch = useAppDispatch()

  const { save, saving } = useSettingsMutation<BrandingSave>({
    mutationFn: async ({ plan }) => {
      // Uploads first, and one at a time: each call writes its own url into the row, so a failure
      // partway leaves the images before it saved and the rest untouched rather than half-applied.
      for (const upload of plan.uploads) await uploadBrandingImage(upload)
      if (plan.patch) await patchBranding(plan.patch)
    },
    invalidate: brandingKey(ecommerceId),
    successKey: 'custom_theme.changes_saved',
    errorKey: 'custom_theme.changes_not_saved',
    onSuccess: ({ editedAttributes }) => {
      posthog?.capture('Theme edited', { ecommerceId, editedAttributes })
      // The rest of the dashboard, and the preview's own fallback, read the theme from settings.
      if (ecommerceId) dispatch(getSettings(ecommerceId))
    }
  })

  // Reports whether the save went through, because a picked image exists only in this page's
  // memory: a caller that cleared it after a failed save would make the merchant find and pick
  // every file again.
  async function callSaveBranding(
    original: BrandingTheme,
    edited: BrandingTheme,
    images: PendingBrandingImages = {}
  ): Promise<boolean> {
    if (!ecommerceId) return false

    const plan = toBrandingSavePlan(ecommerceId, original, edited, images)
    if (plan.uploads.length === 0 && !plan.patch) return true

    return save({
      plan,
      editedAttributes: [
        ...changedBrandingAttributes(original, edited),
        ...Object.keys(images).map((image) => `${image}_image`)
      ]
    })
  }

  return { callSaveBranding, loadingSaveBranding: saving }
}
